import { Inject, Injectable } from '@angular/core';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { determineIsMobile, filterNullUndefined } from '@domgen/dgx-fe-common';
import { GAService } from '@domgen/dgx-fe-ga';
import { ComponentStore } from '@ngrx/component-store';
import {
  RegistrationFacade,
  RegistrationService,
} from '@us-registration/data-access';

import {
  CmsRegistrationOrderConfirmationPage,
  PaymentFrequency,
  PlanDetailsWib,
  REGISTRATION_CONFIG,
  RegistrationConfig,
  RegistrationCPGRequest,
  RegistrationCPGResponse,
  RegistrationRequest,
  RegistriaUpdateUserOrderRequest,
  RegistriaUsersResponse,
} from '@us-registration/util-models';
import {
  combineLatest,
  filter,
  map,
  Observable,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';

export interface ViewModel {
  cmsData: CmsRegistrationOrderConfirmationPage;
  isMobile: boolean;
  registeredAppliance?: RegistrationRequest[];
  registrationCPG?: RegistrationCPGResponse;
  registriaUsersResponse?: RegistriaUsersResponse;
  showRegistrationCPG: boolean;
  planDetails?: PlanDetailsWib;
  paymentFrequency?: PaymentFrequency;
}

export interface ComponentState {
  paymentFrequency?: PaymentFrequency;
  planDetailsAnnualPrice?: number;
  planDetails?: PlanDetailsWib;
  registeredAppliance?: RegistrationRequest[];
  registrationCPG?: RegistrationCPGResponse;
  registrationCPGRequest?: RegistrationCPGRequest;
  registriaUsersResponse?: RegistriaUsersResponse;
  sendRegistriaOrderInfo?: boolean;
  showRegistrationCPG: boolean;
}

@Injectable()
export class RegistrationOrderConfirmationWibComponentService extends ComponentStore<ComponentState> {
  private readonly registeredAppliance$ = this.select(
    (state) => state.registeredAppliance
  );

  private readonly registriaUsersResponse$: Observable<
    RegistriaUsersResponse | undefined
  > = this.select((state) => state.registriaUsersResponse);

  private readonly registrationCPGRequest$: Observable<
    RegistrationCPGRequest | undefined
  > = this.select((state: ComponentState) => state.registrationCPGRequest);

  private readonly registrationCPG$: Observable<
    RegistrationCPGResponse | undefined
  > = this.select((state: ComponentState) => state.registrationCPG);

  private readonly sendRegistriaOrderInfo$: Observable<boolean | undefined> =
    this.select((state) => state.sendRegistriaOrderInfo);

  private readonly showRegistrationCPG$: Observable<boolean> = this.select(
    (state: ComponentState) => state.showRegistrationCPG
  );

  private readonly planDetails$: Observable<PlanDetailsWib | undefined> =
    this.select((state: ComponentState) => state.planDetails);

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    isMobile: determineIsMobile(),
    registeredAppliance: this.registeredAppliance$,
    registrationCPG: this.registrationCPG$,
    registriaUsersResponse: this.registriaUsersResponse$,
    showRegistrationCPG: this.showRegistrationCPG$,
    planDetails: this.planDetails$,
    paymentMonthlyOrAnnual: this.registrationFacade.paymentFrequency$,
  });

  constructor(
    @Inject(REGISTRATION_CONFIG)
    private readonly registrationConfig: RegistrationConfig,
    private readonly cmsContainer: CmsContainer<CmsRegistrationOrderConfirmationPage>,
    private readonly registrationService: RegistrationService,
    private readonly gaService: GAService,
    private readonly registrationFacade: RegistrationFacade
  ) {
    super({
      showRegistrationCPG: false,
    });
  }

  readonly updateRegistriaUserResponse = this.updater(
    (
      state: ComponentState,
      registriaUsersResponse?: RegistriaUsersResponse
    ) => ({
      ...state,
      registriaUsersResponse,
    })
  )(this.registrationFacade.registriaUserResponse$);

  readonly updateRegisteredAppliance = this.updater(
    (state: ComponentState, registeredAppliance?: RegistrationRequest) => ({
      ...state,
      registeredAppliance: registeredAppliance
        ? [registeredAppliance]
        : undefined,
    })
  )(this.registrationFacade.registrationRequest$);

  updateRegistrationCPG = this.updater(
    (state: ComponentState, registrationCPG?: RegistrationCPGResponse) => ({
      ...state,
      registrationCPG,
    })
  );

  readonly updateSendRegistriaOrderInfo = this.updater(
    (state: ComponentState, sendRegistriaOrderInfo?: boolean) => ({
      ...state,
      sendRegistriaOrderInfo,
    })
  )(this.registrationConfig.features.sendRegistriaOrderInfo);

  readonly updateShowRegistrationCPG = this.updater(
    (state: ComponentState, showRegistrationCPG: boolean) => ({
      ...state,
      showRegistrationCPG,
    })
  )(
    this.registrationCPG$.pipe(
      map((registrationCPG) => typeof registrationCPG !== 'string')
    )
  );

  readonly updatePlanDetails = this.updater(
    (state: ComponentState, planDetails: PlanDetailsWib | undefined) => ({
      ...state,
      planDetails,
    })
  )(
    combineLatest([
      this.registrationFacade.checkoutSuccessfulContracts$,
      this.registrationFacade.effectiveDate$,
      this.registrationFacade.contractId$,
      this.registrationFacade.checkoutInitialisation$,
      this.registrationFacade.paymentFrequency$,
    ]).pipe(
      map(
        ([
          checkoutSuccessfulContract,
          effectiveDate,
          contractId,
          checkoutInitialisation,
          paymentFrequency,
        ]) => {
          if (!contractId || !effectiveDate) {
            return undefined;
          }

          const price =
            checkoutInitialisation?.basket?.offers[0].contracts[0]
              .contractAttributes.cc?.netTotalAmount;
          return {
            checkoutSuccessfulContract,
            planDetailsAnnualPrice: price,
            effectiveDate,
            planNumber: contractId,
            price,
            paymentFrequency,
          };
        }
      )
    )
  );

  readonly updateRegistrationCPGRequest = this.updater(
    (
      state: ComponentState,
      registrationCPGRequest: RegistrationCPGRequest | undefined
    ) => ({
      ...state,
      registrationCPGRequest,
    })
  )(
    this.registeredAppliance$.pipe(
      filterNullUndefined(),
      map((registeredAppliances) => ({
        modelNumber: String(registeredAppliances[0].merchandise[0].modelNumber),
      }))
    )
  );

  private readonly getRegistrationCPGOffer: Subscription = this.effect(
    (trigger$: Observable<RegistrationCPGRequest | undefined>) =>
      trigger$.pipe(
        filterNullUndefined(),
        switchMap((registrationCPGRequest) =>
          this.registrationService.getApplianceCPG(registrationCPGRequest)
        ),
        tap((registrationCPGResponse) =>
          this.updateRegistrationCPG(
            registrationCPGResponse as RegistrationCPGResponse
          )
        )
      )
  )(this.registrationCPGRequest$);

  private readonly updateRegistriaUserWithOrder: Subscription = this.effect(
    (
      trigger$: Observable<
        [
          PlanDetailsWib | undefined,
          RegistriaUsersResponse | undefined,
          boolean | undefined
        ]
      >
    ) =>
      trigger$.pipe(
        filter(
          ([planDetails, registriaUsersResponse, sendRegistriaOrderInfo]) =>
            sendRegistriaOrderInfo === true
        ),
        map(
          ([planDetails, registriaUsersResponse, sendRegistriaOrderInfo]):
            | RegistriaUpdateUserOrderRequest
            | undefined => {
            if (!planDetails || !registriaUsersResponse) {
              return;
            }
            return {
              orderInfo: {
                sessionId: registriaUsersResponse.sessionId,
                orderNumber: planDetails.planNumber,
                amount: String(planDetails.price),
                orderDate: planDetails.effectiveDate,
              },
            };
          }
        ),
        filterNullUndefined(),
        switchMap((updateUserOrderRequest: RegistriaUpdateUserOrderRequest) => {
          return this.registrationService.registriaUsers(
            updateUserOrderRequest
          );
        })
      )
  )(
    combineLatest([
      this.planDetails$,
      this.registriaUsersResponse$,
      this.sendRegistriaOrderInfo$,
    ])
  );

  private readonly trackOrderConfirmationGAPageView: Subscription = this.effect(
    (
      trigger$: Observable<
        [RegistrationRequest | undefined, PlanDetailsWib | undefined]
      >
    ) =>
      trigger$.pipe(
        tap(([registeredAppliance, planDetails]) => {
          if (registeredAppliance && planDetails) {
            this.gaService.pageView({
              pagename: 'order-confirmation',
              applianceName: registeredAppliance.merchandise[0].type,
              appliancePurchaseCost:
                registeredAppliance.merchandise[0].purchasePrice,
              appliancePurchaseDate:
                registeredAppliance.merchandise[0].purchaseDate,
              offerGeneratedPrice: planDetails.price,
              planNumber: planDetails.planNumber,
              regSystemCode: localStorage.getItem('systemCode'),
              codeUsed: localStorage.getItem('registrationCodeUsed'),
            });
          }
        })
      )
  )(
    combineLatest([
      this.registrationFacade.registrationRequest$,
      this.planDetails$,
    ])
  );

  private readonly trackGAPurchaseEvent: Subscription = this.effect(
    (
      trigger$: Observable<
        [RegistrationRequest | undefined, PlanDetailsWib | undefined]
      >
    ) =>
      trigger$.pipe(
        tap(([registeredAppliance, planDetails]) => {
          const hasTrackedGAPurchaseEvent: boolean =
            localStorage.getItem('hasTrackedGAPurchaseEvent') === 'true';

          if (
            registeredAppliance &&
            planDetails &&
            !hasTrackedGAPurchaseEvent &&
            planDetails.checkoutContract
          ) {
            this.gaService.tag({
              event: 'purchase',
              ecommerce: {
                currency: 'US Dollar ($)',
                value: planDetails.price,
                transaction_id: localStorage.getItem('itbId'),
                regSystemCode: localStorage.getItem('systemCode'),
                items: [
                  {
                    appliance_purchase_cost:
                      registeredAppliance.merchandise[0].purchasePrice,
                    appliance_purchase_date:
                      registeredAppliance.merchandise[0].purchaseDate,
                    appliance_type: registeredAppliance.merchandise[0].type,
                    appliance_warranty: true,
                    item_brand: registeredAppliance.merchandise[0].brand,
                    item_category: registeredAppliance.merchandise[0].type,
                    item_id: planDetails.checkoutContract.itemId,

                    item_name: `${registeredAppliance.merchandise[0].brand} ${registeredAppliance.merchandise[0].type}`,
                    paymentType: 'D',
                    price: planDetails.price,
                    quantity: 1,
                  },
                ],
              },
            });
            localStorage.removeItem('itbId');
            localStorage.setItem('hasTrackedGAPurchaseEvent', 'true');
          }
        })
      )
  )(
    combineLatest([
      this.registrationFacade.registrationRequest$,
      this.planDetails$,
    ])
  );
}
